import { z } from 'zod';

export const RECURRING_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const money = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');

export const recurringLineInputSchema = z.object({
  accountId: z.string().uuid(),
  debit: money.default('0'),
  credit: money.default('0'),
  memo: z.string().max(500).nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
});
export type RecurringLineInput = z.input<typeof recurringLineInputSchema>;

const dayOfMonth = z.number().int().min(1).max(28);

export const createRecurringEntryInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  frequency: z.enum(RECURRING_FREQUENCIES),
  interval: z.number().int().min(1).default(1),
  /** Required-ish for monthly/quarterly/yearly; derived from startDate (clamped to 28) when omitted. Ignored for weekly. */
  dayOfMonth: dayOfMonth.nullable().optional(),
  startDate: isoDate,
  endDate: isoDate.nullable().optional(),
  maxRuns: z.number().int().min(1).nullable().optional(),
  memoTemplate: z.string().max(500).nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  lines: z.array(recurringLineInputSchema).min(2),
});
export type CreateRecurringEntryInput = z.input<typeof createRecurringEntryInputSchema>;

/** Partial update — only provided fields change; if lines are provided they replace the set. */
export const updateRecurringEntryInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  frequency: z.enum(RECURRING_FREQUENCIES).optional(),
  interval: z.number().int().min(1).optional(),
  dayOfMonth: dayOfMonth.nullable().optional(),
  startDate: isoDate.optional(),
  endDate: isoDate.nullable().optional(),
  maxRuns: z.number().int().min(1).nullable().optional(),
  memoTemplate: z.string().max(500).nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  isActive: z.boolean().optional(),
  lines: z.array(recurringLineInputSchema).min(2).optional(),
});
export type UpdateRecurringEntryInput = z.input<typeof updateRecurringEntryInputSchema>;

export const setRecurringActiveInputSchema = z.object({ isActive: z.boolean() });
export type SetRecurringActiveInput = z.infer<typeof setRecurringActiveInputSchema>;

export const listRecurringEntriesQuerySchema = z.object({
  isActive: z.enum(['true', 'false']).optional(),
});
export type ListRecurringEntriesQuery = z.infer<typeof listRecurringEntriesQuerySchema>;

export interface RecurringEntry {
  id: string;
  companyId: string;
  name: string;
  frequency: RecurringFrequency;
  interval: number;
  dayOfMonth: number | null;
  startDate: string;
  nextRunDate: string;
  endDate: string | null;
  maxRuns: number | null;
  runCount: number;
  isActive: boolean;
  /** Derived: active, within limits, and nextRunDate is on/before today. */
  isDue: boolean;
  memoTemplate: string | null;
  reference: string | null;
  lastRunAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecurringEntryLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: string;
  credit: string;
  memo: string | null;
}

export interface RecurringEntryWithLines extends RecurringEntry {
  lines: RecurringEntryLine[];
}

export interface RunRecurringResult {
  recurringEntryId: string;
  generated: boolean;
  journalEntryId: string | null;
  error: string | null;
}

export interface GenerateRecurringResult {
  asOf: string;
  generated: number;
  failed: number;
  results: RunRecurringResult[];
}
